import React, { useState, useEffect } from 'react';
import { Form, Input, Button, DatePicker, InputNumber, Card, Space, message, Spin, Table, Select, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import api from '../../services/api';
import localforage from 'localforage';
import {
  Store,
  PurchaseOrder,
  PurchaseItem,
  PurchaseOrderFormData,
  PriceValue,
  QuantityValue,
  FieldValue
} from '../../types';

const { Option } = Select;

const PurchaseOrderForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState<PurchaseItem[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const isEditing = !!id;

  // 自動保存的鍵
  const getAutoSaveKey = () => `purchase_order_form_${id || 'new'}`;

  // 加載採購訂單數據
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 獲取所有店鋪
        const storesData = await api.stores.getAll() as Store[];
        setStores(storesData);

        if (isEditing) {
          // 獲取採購訂單
          const orderData = await api.purchaseOrders.getById(id as string) as PurchaseOrder;

          // 處理日期字段
          if (orderData.purchase_date) {
            // 將 purchase_date 轉換為 Moment 對象，但保持類型兼容性
            (orderData as any).purchase_date = moment(orderData.purchase_date);
          }

          form.setFieldsValue(orderData);

          // 獲取採購項目
          const itemsData = await api.purchaseOrders.getItems(id as string) as PurchaseItem[];
          setItems(itemsData);
        } else {
          // 嘗試從本地存儲加載草稿
          const savedData = await localforage.getItem(getAutoSaveKey()) as { orderData?: Partial<PurchaseOrder>; itemsData?: PurchaseItem[] } | null;
          if (savedData) {
            const { orderData, itemsData } = savedData;

            if (orderData) {
              // 處理日期字段
              if (orderData.purchase_date) {
                (orderData as any).purchase_date = moment(orderData.purchase_date);
              }

              form.setFieldsValue(orderData);
            }

            if (itemsData) {
              setItems(itemsData);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        message.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, form, isEditing, t]);

  // 自動保存表單數據
  useEffect(() => {
    const autoSaveInterval = setInterval(async () => {
      const formValues = form.getFieldsValue();
      if (Object.keys(formValues).length > 0) {
        await localforage.setItem(getAutoSaveKey(), {
          orderData: formValues,
          itemsData: items,
        });
      }
    }, 30000); // 每 30 秒保存一次

    return () => clearInterval(autoSaveInterval);
  }, [form, items]);

  // 添加採購項目
  const handleAddItem = () => {
    const newItem: PurchaseItem = {
      id: `temp_${Date.now()}`,
      purchase_order_id: id || 'new',
      product_name: '',
      price: 0,
      quantity: 1,
      subtotal: 0,
    };
    setItems([...items, newItem]);
  };

  // 刪除採購項目
  const handleDeleteItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  // 更新採購項目
  const handleItemChange = <K extends keyof PurchaseItem>(
    index: number,
    field: K,
    value: FieldValue<PurchaseItem[K]>
  ) => {
    const newItems = [...items];
    (newItems[index] as any)[field] = value;

    // 如果更新了價格或數量，重新計算小計
    if (field === 'price' || field === 'quantity') {
      const price = field === 'price'
        ? value as PriceValue
        : newItems[index].price;
      const quantity = field === 'quantity'
        ? value as QuantityValue
        : newItems[index].quantity;

      // 確保價格和數量都有值才計算小計
      if (price !== null && quantity !== null) {
        newItems[index].subtotal = price * quantity;
      }
    }

    setItems(newItems);
  };

  // 計算總金額
  const calculateTotalAmount = () => {
    return items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
  };

  // 提交表單
  const handleSubmit = async (values: PurchaseOrderFormData) => {
    // 檢查是否有採購項目
    if (items.length === 0) {
      message.error(t('purchaseOrders.items') + ' ' + t('common.error'));
      return;
    }

    // 檢查採購項目是否填寫完整
    const invalidItem = items.find(item => !item.product_name || item.price <= 0 || item.quantity <= 0);
    if (invalidItem) {
      message.error(t('purchaseOrders.items') + ' ' + t('common.error'));
      return;
    }

    setSubmitting(true);
    try {
      // 處理日期字段和總金額
      const formattedValues = {
        ...values,
        purchase_date: values.purchase_date ? values.purchase_date.format('YYYY-MM-DD') : undefined,
        total_amount: calculateTotalAmount()
      };

      let orderId: string;
      if (isEditing && id) {
        await api.purchaseOrders.update(id, formattedValues);
        orderId = id;
      } else {
        const result = await api.purchaseOrders.create(formattedValues);
        orderId = result.id;
      }

      // 處理採購項目
      // 注意：這裡假設 API 支持批量處理項目，實際情況可能需要調整
      await Promise.all(items.map(item => {
        const itemData = {
          purchase_order_id: orderId,
          product_name: item.product_name,
          price: item.price,
          quantity: item.quantity,
          subtotal: item.subtotal,
        };

        // 這裡假設 API 有這些方法，如果沒有，需要在 api 服務中添加
        if (item.id && !item.id.toString().startsWith('temp_')) {
          return (api.purchaseOrders as any).updateItem(item.id, itemData);
        } else {
          return (api.purchaseOrders as any).createItem(itemData);
        }
      }));

      // 清除本地存儲的草稿
      await localforage.removeItem(getAutoSaveKey());

      message.success(t('common.success'));
      navigate('/purchase-orders');
    } catch (error) {
      console.error('Error saving purchase order:', error);
      message.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // 採購項目表格列
  const itemColumns = [
    {
      title: t('purchaseOrders.productName'),
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text: string, _record: PurchaseItem, index: number) => (
        <Input
          value={text}
          onChange={(e) => handleItemChange(index, 'product_name', e.target.value)}
          placeholder={t('purchaseOrders.productName')}
        />
      ),
    },
    {
      title: t('purchaseOrders.price'),
      dataIndex: 'price',
      key: 'price',
      render: (text: number, _record: PurchaseItem, index: number) => (
        <InputNumber
          value={text}
          onChange={(value) => handleItemChange(index, 'price', value)}
          min={0}
          precision={2}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('purchaseOrders.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (text: number, _record: PurchaseItem, index: number) => (
        <InputNumber
          value={text}
          onChange={(value) => handleItemChange(index, 'quantity', value)}
          min={1}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('purchaseOrders.subtotal'),
      dataIndex: 'subtotal',
      key: 'subtotal',
    },
    {
      title: t('common.action'),
      key: 'action',
      render: (_text: any, _record: PurchaseItem, index: number) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteItem(index)}
        />
      ),
    },
  ];

  if (loading) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(isEditing ? `/purchase-orders/${id}` : '/purchase-orders')}
        >
          {t('common.back')}
        </Button>
      </Space>

      <Card title={isEditing ? t('purchaseOrders.editOrder') : t('purchaseOrders.addOrder')}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            courier_status: '未填寫',
          }}
        >
          <Form.Item
            name="order_id"
            label={t('purchaseOrders.orderId')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="store_id"
            label={t('purchaseOrders.storeId')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Select
              showSearch
              placeholder={t('purchaseOrders.storeId')}
              optionFilterProp="children"
            >
              {stores.map(store => (
                <Option key={store.store_id} value={store.store_id}>
                  {store.store_id} - {store.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="platform"
            label={t('purchaseOrders.platform')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="purchase_date"
            label={t('purchaseOrders.purchaseDate')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="contact"
            label={t('purchaseOrders.contact')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="courier_number"
            label={t('purchaseOrders.courierNumber')}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="courier_status"
            label={t('purchaseOrders.courierStatus')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Select>
              <Option value="已填寫">{t('purchaseOrders.courierStatuses.filled')}</Option>
              <Option value="未填寫">{t('purchaseOrders.courierStatuses.unfilled')}</Option>
            </Select>
          </Form.Item>

          <Divider orientation="left">{t('purchaseOrders.items')}</Divider>

          <Button
            type="dashed"
            onClick={handleAddItem}
            style={{ width: '100%', marginBottom: 16 }}
            icon={<PlusOutlined />}
          >
            {t('purchaseOrders.addItem')}
          </Button>

          <Table
            columns={itemColumns}
            dataSource={items}
            rowKey="id"
            pagination={false}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>{t('purchaseOrders.totalAmount')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <strong>{calculateTotalAmount()}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
              </Table.Summary.Row>
            )}
          />

          <Form.Item style={{ marginTop: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              icon={<SaveOutlined />}
            >
              {t('common.save')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default PurchaseOrderForm;
